package com.essntl.features.itinerary.data.supabase.itinerary_service

import com.essntl.features.itinerary.domain.model.ItineraryServiceModel
import core.config.CurrencyConfig
import core.dateformat.utils.fromDDTOMM
import kotlinx.serialization.ExperimentalSerializationApi
import kotlinx.serialization.json.*
import kotlin.math.max

@OptIn(ExperimentalSerializationApi::class)
internal fun ItineraryServiceModel.toRequestMap(ignoreNullFields: Boolean = true): Map<String, JsonElement> {
    val requestMap = mutableMapOf<String, JsonElement>()

    requestMap["service_id"] = JsonPrimitive(service.id)

    if (supplierContact != null || !ignoreNullFields) {
        requestMap["supplier_contact_id"] = JsonPrimitive(supplierContact?.id?.ifBlank { null })
    }

    if (staticPrice != null || !ignoreNullFields) {
        requestMap["static_price_id"] = JsonPrimitive(staticPrice?.id)
    }

    if (grouped != null || !ignoreNullFields) {
        requestMap["grouped"] = JsonPrimitive(grouped ?: false)
    }

    if (additionalInfo != null || !ignoreNullFields) {
        requestMap["additional_info"] = JsonPrimitive(additionalInfo ?: "")
    }

    if (editedDescription != null || !ignoreNullFields) {
        requestMap["edited_description"] = JsonPrimitive(editedDescription ?: "")
    }

    if (startDate.isNotEmpty() || !ignoreNullFields) {
        requestMap["start_date"] = JsonPrimitive(fromDDTOMM(startDate))
    }

    if (endDate != null || !ignoreNullFields) {
        endDate?.let {
            requestMap["end_date"] = JsonPrimitive(
                fromDDTOMM(it).ifBlank { fromDDTOMM(startDate).ifBlank { null } },
            )
        }
    }

    if (startTime != null || !ignoreNullFields) {
        requestMap["start_time"] = JsonPrimitive(startTime?.ifBlank { null })
    }

    if (endTime != null || !ignoreNullFields) {
        requestMap["end_time"] = JsonPrimitive(endTime?.ifBlank { null })
    }

    if (price != null || !ignoreNullFields) {
        requestMap["price"] = JsonPrimitive(price)
        requestMap["price_currency"] = JsonPrimitive(priceCurrency?.code ?: CurrencyConfig.defaultCurrencyCode)
    }

    if (internalNotes != null || !ignoreNullFields) {
        requestMap["internal_notes"] = JsonPrimitive(internalNotes)
    }

    if (status != null) {
        requestMap["status"] = JsonPrimitive(status?.toDto())
    }

    if (supplier != null || !ignoreNullFields) {
        requestMap["supplier_id"] = JsonPrimitive(supplier?.id)
    }

    if (guestCount != -1 || !ignoreNullFields) {
        requestMap["guest_count"] = JsonPrimitive(max(1, guestCount))
    }

    if (address != null || !ignoreNullFields) {
        address?.id?.let { id ->
            if (id.isNotEmpty())
                requestMap["address_id"] = JsonPrimitive(id)
            else
                requestMap["address_id"] = JsonPrimitive(null)
        }
    }

    if (meetingPoint != null || !ignoreNullFields) {
        requestMap["address"] = JsonPrimitive(meetingPoint)
    }

    if (!dynamicFields.isNullOrEmpty() || !ignoreNullFields) {
        requestMap["dynamic_fields"] = JsonArray(
            dynamicFields
                .orEmpty()
                .map { Json.encodeToJsonElement<DynamicFieldDto>(it.toDto()) },
        )
    }

    if (itineraryId != null || !ignoreNullFields) {
        requestMap["itinerary_id"] = JsonPrimitive(itineraryId)
    }

    if (specialNotes != null || !ignoreNullFields) {
        requestMap["special_notes"] = JsonPrimitive(specialNotes)
    }

    if (paid != null || !ignoreNullFields) {
        requestMap["paid"] = JsonPrimitive(paid ?: false)
    }

    if (embeddedMap != null || !ignoreNullFields) {
        requestMap["embedded_map"] = JsonPrimitive(embeddedMap ?: "")
    }

    if (embeddedContent != null || !ignoreNullFields) {
        requestMap["embedded_content"] = JsonPrimitive(embeddedContent ?: "")
    }

    if (files != null) {
        requestMap["files"] = JsonArray(files!!.map { JsonPrimitive(it) })
    }

    if (linkedId != null || !ignoreNullFields) {
        requestMap["linked_id"] = JsonPrimitive(linkedId?.ifBlank { null })
    }

    if (poSentAt != null) {
        requestMap["po_sent_at"] = JsonPrimitive(poSentAt!!)
    }

    if (bookedByTa != null || !ignoreNullFields) {
        requestMap["booked_by_ta"] = JsonPrimitive(bookedByTa ?: false)
    }

    if (cost != null || !ignoreNullFields) {
        requestMap["cost"] = JsonPrimitive(cost)
        requestMap["cost_currency"] = JsonPrimitive(costCurrency?.code ?: CurrencyConfig.defaultCurrencyCode)
    }

    if (priceCurrency != null || !ignoreNullFields) {
        requestMap["price_currency"] = JsonPrimitive(priceCurrency?.code)
    }

    if (costCurrency != null || !ignoreNullFields) {
        requestMap["cost_currency"] = JsonPrimitive(costCurrency?.code)
    }

    return requestMap
}
